import { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  Image,
  Animated,
  Pressable,
  StatusBar,
  Platform,
  useWindowDimensions,
} from 'react-native'
import {
  View,
  Text,
  XStack,
  styled,
  YStack,
  useMedia,
  Spinner,
  Sheet,
} from 'tamagui'
import { VideoView, useVideoPlayer } from 'expo-video'
import { HeroIcon } from '../HeroIcon'
import { useEvent, useEventListener } from 'expo'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { RangeInput } from '../RangeInput'
import { LinearGradient } from 'expo-linear-gradient'
import { AutoPlaySwitch } from './AutoPlaySwitch'
import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import { VIDEO_AUTO_PLAY } from '@constants/storages'
import { ConfigsModal } from './ConfigsModal'
import { QualitySelector } from './QualitySelector'
import { PlaybackRateSelector } from './PlaybackRateSelector'
import { useFullScreenModeStore } from '@stores/useFullScreenModeStore'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useComponentTranslation } from '@hooks/useComponentTranslation'
import {
  ISparkVideosUrls,
  IUpdateLessonProgress,
} from '@components/MediaRender'
import * as NavigationBar from 'expo-navigation-bar'
import { THEME_LIGHT_MODE, VIDEO_WATERMARK } from '@constants/features'
import { useHasFeature } from '@hooks/useThemeParams'
import { Watermark } from './Watermark'
import { useVideoRotation } from '@hooks/useVideoRotation'
import { useSession } from '@hooks/useSession'

export type QualityOption = {
  label: string
  value: string
  resolution: string
  bandwidth: number
}

export type PlaybackRateOption = {
  label: string
  value: number
}

export const PLAYBACK_RATE_OPTIONS: PlaybackRateOption[] = [
  { label: '0.25', value: 0.25 },
  { label: '1.0', value: 1 },
  { label: '1.25', value: 1.25 },
  { label: '1.5', value: 1.5 },
  { label: '2.0', value: 2 },
]

interface IVideoPlayerProps {
  videoUrl: ISparkVideosUrls
  mediaUrl: string
  title: string
  videoContainerHeight?: number
  qualityOptions: QualityOption[]
  thumbnailUrl?: string
  isVertical: boolean
  updateProgress: (params: IUpdateLessonProgress) => void
  actualProgress?: number
  videoStartTime?: number
}

export const VideoPlayer: React.FC<IVideoPlayerProps> = ({
  videoUrl,
  mediaUrl,
  title,
  videoContainerHeight = 240,
  qualityOptions,
  thumbnailUrl,
  isVertical,
  updateProgress,
  actualProgress = 0,
  videoStartTime,
}) => {
  const [showQualitySelector, setShowQualitySelector] = useState(false)
  const [currentQuality, setCurrentQuality] = useState<QualityOption>(
    qualityOptions[0],
  )
  const [isLoading, setIsLoading] = useState(false)
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false)
  const [isAutoPlayActive, setIsAutoPlayActive] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [showVolumeSelector, setShowVolumeSelector] = useState(false)
  const [showPlaybackRateSelector, setShowPlaybackRateSelector] =
    useState(false)
  const [volume, setVolume] = useState(50)
  const [playbackRate, setPlaybackRate] = useState(1)
  const [progressMarkers, setProgressMarkers] = useState({
    25: actualProgress >= 25,
    50: actualProgress >= 50,
    75: actualProgress >= 75,
    100: actualProgress >= 100,
  })
  const fadeAnim = useRef(new Animated.Value(1)).current
  const controlsTimeout = useRef<NodeJS.Timeout>()
  const previewTimeout = useRef<NodeJS.Timeout>()
  const videoRef = useRef<VideoView>(null)
  const videoEnded = useRef(false)
  const isMuted = useRef(false)
  const [showConfigsModal, setShowConfigsModal] = useState(false)
  const media = useMedia()
  const { setItem, getItem, removeItem } = useAsyncStorage(VIDEO_AUTO_PLAY)
  const {
    setIsFullScreenMode,
    isFullScreenMode,
    setVideoDefaultProps,
    videoDefaultProps,
  } = useFullScreenModeStore()
  const safeAreaInsets = useSafeAreaInsets()
  const { t } = useComponentTranslation('VideoPlayer')
  const hasFeature = useHasFeature(VIDEO_WATERMARK)
  const isLightMode = useHasFeature(THEME_LIGHT_MODE)
  const videoStyles = useVideoRotation({ isFullScreenMode, isVertical })
  const { height } = useWindowDimensions()
  const safeArea = useSafeAreaInsets()
  const { user } = useSession()

  const player = useVideoPlayer(
    {
      uri: currentQuality.value,
      metadata: {
        artwork: thumbnailUrl || '',
        title,
      },
    },
    async (player) => {
      player.timeUpdateEventInterval = 1
      if (await getItem()) {
        player.currentTime = videoDefaultProps.timeToResume || 0
        setTimeout(() => {
          player.play()
          setHasStartedPlaying(true)
        }, 1000)
      }
    },
  )

  useEffect(() => {
    if (isFullScreenMode) {
      StatusBar.setHidden(true)
      if (Platform.OS === 'android') {
        NavigationBar.setVisibilityAsync('hidden')
      }
    } else {
      StatusBar.setHidden(false)
      if (Platform.OS === 'android') {
        NavigationBar.setVisibilityAsync('visible')
      }
    }
  }, [isFullScreenMode])

  useEventListener(player, 'statusChange', (event) => {
    if (event.status === 'readyToPlay' && !hasStartedPlaying) {
      resetVideoDefaultProps()
    }
    if (event.status === 'loading') {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  })

  useEventListener(player, 'playToEnd', () => {
    videoEnded.current = true
    setVideoDefaultProps({
      ...videoDefaultProps,
      isVideoEnded: true,
    })
    setShowControls(true)
  })

  const { isPlaying } = useEvent(player, 'playingChange', {
    isPlaying: player.playing,
  })

  const timeUpdate = useEvent(player, 'timeUpdate', {
    currentTime: 0,
    currentLiveTimestamp: null,
    currentOffsetFromLive: null,
    bufferedPosition: 0,
  })

  useEffect(() => {
    if (isPlaying && !hasStartedPlaying) {
      setHasStartedPlaying(true)
    }
  }, [hasStartedPlaying, isPlaying])

  const fullscreenHeight = useMemo(() => {
    if (Platform.OS === 'ios') {
      return height
    }
    return height + safeArea.top
  }, [height, safeArea.top])

  const showLoading = useMemo(() => {
    return isLoading && !hasStartedPlaying
  }, [hasStartedPlaying, isLoading])

  const { currentTime, duration } = useMemo(() => {
    return {
      currentTime: hasStartedPlaying
        ? player.currentTime
        : videoDefaultProps.timeToResume || 0,
      duration: player.duration || 0,
    }
  }, [
    player.currentTime,
    player.duration,
    hasStartedPlaying,
    videoDefaultProps.timeToResume,
  ])

  const bufferedPosition = useMemo(() => {
    return timeUpdate?.bufferedPosition || 0
  }, [timeUpdate?.bufferedPosition])

  const bufferedPlataformStyles = () => {
    const isIOS = Platform.OS === 'ios'
    const translateX = isIOS ? 4 : 15.5
    const translateY = isIOS ? -2.3 : -1.3
    const height = isIOS ? 4 : 2.3
    const width = isIOS ? 4 : 10
    return {
      transform: [{ translateY }, { translateX }],
      height,
      width,
    }
  }

  const bufferedPercentage = useMemo(() => {
    return Math.max(0, (bufferedPosition / Math.max(duration, 1)) * 100)
  }, [bufferedPosition, duration])

  const progressThresholds = useMemo(
    () => [
      {
        threshold: 25,
        onProgress: () =>
          updateProgress({
            progress: 25,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 25,
      },
      {
        threshold: 50,
        onProgress: () =>
          updateProgress({
            progress: 50,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 50,
      },
      {
        threshold: 75,
        onProgress: () =>
          updateProgress({
            progress: 75,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 75,
      },
      {
        threshold: 100,
        onProgress: () =>
          updateProgress({
            progress: 100,
            currentVideoTime: Math.floor(currentTime),
          }),
        key: 100,
      },
    ],
    [currentTime, updateProgress],
  )

  useEventListener(player, 'timeUpdate', ({ currentTime }) => {
    if (!duration) return
    const progress = (currentTime / duration) * 100

    progressThresholds.forEach(({ threshold, onProgress, key }) => {
      if (
        !progressMarkers[key as keyof typeof progressMarkers] &&
        progress >= threshold
      ) {
        setProgressMarkers((prev) => ({ ...prev, [key]: true }))
        onProgress?.()
      }
    })
  })

  useEffect(() => {
    if (isPlaying && showControls) {
      startControlsTimer()
    } else if (!isPlaying) {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current)
      }
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 0,
        useNativeDriver: true,
      }).start()
    }
    return () => {
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fadeAnim, isPlaying, showControls])

  useEffect(() => {
    getItem().then((value) => {
      setIsAutoPlayActive(!!value)
    })
  }, [getItem])

  const startControlsTimer = () => {
    if (controlsTimeout.current) {
      clearTimeout(controlsTimeout.current)
    }

    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 0,
      useNativeDriver: true,
    }).start()

    controlsTimeout.current = setTimeout(() => {
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 300,
        useNativeDriver: true,
      }).start(() => setShowControls(false))
    }, 3000)
  }

  const handleQualityChange = useCallback(
    async (newQuality: QualityOption) => {
      if (newQuality.value === currentQuality.value) {
        return
      }
      try {
        setIsLoading(true)
        const timeToResume = player.currentTime
        setCurrentQuality({ ...newQuality })
        setVideoDefaultProps({
          ...videoDefaultProps,
          timeToResume,
        })
      } catch (error) {
        throw new Error(t('error.qualityChange', { error }))
      } finally {
        setIsLoading(false)
      }
    },
    [
      currentQuality.value,
      player.currentTime,
      setVideoDefaultProps,
      t,
      videoDefaultProps,
    ],
  )

  const resetVideoDefaultProps = useCallback(() => {
    setVideoDefaultProps({
      videoUrl,
      mediaUrl,
      title,
      thumbnailUrl: thumbnailUrl || '',
      timeToResume: videoStartTime || 0,
      isVideoEnded: false,
      isVertical,
      isPlaying,
      hasStartedPlaying,
      qualityOptions,
    })
  }, [
    setVideoDefaultProps,
    videoUrl,
    mediaUrl,
    title,
    thumbnailUrl,
    videoStartTime,
    isVertical,
    isPlaying,
    hasStartedPlaying,
    qualityOptions,
  ])

  const resetProgressMarkers = useCallback(
    (newTime: number) => {
      const progress = (newTime / duration) * 100
      const updatedMarkers = { ...progressMarkers }

      progressThresholds.forEach(({ threshold, key }) => {
        if (progress < threshold) {
          updatedMarkers[key as keyof typeof progressMarkers] = false
        }
      })

      setProgressMarkers(updatedMarkers)
    },
    [duration, progressThresholds, progressMarkers],
  )

  const handleProgressChange = useCallback(
    (value: number) => {
      const newTime = (value / 100) * duration

      if (previewTimeout.current) {
        clearTimeout(previewTimeout.current)
      }

      resetProgressMarkers(newTime)

      player.currentTime = newTime
    },
    [duration, player, resetProgressMarkers],
  )

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      player.pause()
      setVideoDefaultProps({
        ...videoDefaultProps,
        timeToResume: player.currentTime,
      })
      if (controlsTimeout.current) {
        clearTimeout(controlsTimeout.current)
      }
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 0,
        useNativeDriver: true,
      }).start()
      setShowControls(true)
    } else if (videoEnded.current || videoDefaultProps.isVideoEnded) {
      setVideoDefaultProps({
        ...videoDefaultProps,
        timeToResume: 0,
        isVideoEnded: false,
      })
      player.currentTime = 0
      player.play()
      videoEnded.current = false
      setShowControls(true)
    } else {
      if (videoStartTime && !hasStartedPlaying) {
        player.currentTime = videoStartTime
      }
      if (videoDefaultProps.timeToResume) {
        player.currentTime = videoDefaultProps.timeToResume
      }
      player.play()
    }
  }, [
    isPlaying,
    videoDefaultProps,
    player,
    setVideoDefaultProps,
    fadeAnim,
    videoStartTime,
    hasStartedPlaying,
  ])

  const handleSkip = useCallback(
    (seconds: number) => {
      const newTime = Math.min(Math.max(currentTime + seconds, 0), duration)

      if (seconds < 0) {
        resetProgressMarkers(newTime)
      }

      player.currentTime = newTime
    },
    [currentTime, duration, player, resetProgressMarkers],
  )

  const rangeColor = useMemo(() => {
    return isLightMode ? '#7427F1' : '#00E4F5'
  }, [isLightMode])

  const playbackRateOptions: PlaybackRateOption[] = useMemo(() => {
    return PLAYBACK_RATE_OPTIONS
  }, [])

  const handlePlaybackRateChange = useCallback(
    (rate: number) => {
      setPlaybackRate(rate)
      player.playbackRate = rate
    },
    [player],
  )

  useEffect(() => {
    if (!isFullScreenMode) {
      player.currentTime = videoDefaultProps.timeToResume || 0
    }
  }, [isFullScreenMode, videoDefaultProps.timeToResume, player])

  const handleFullScreen = useCallback(() => {
    setIsFullScreenMode(!isFullScreenMode)
    setVideoDefaultProps({
      videoUrl,
      mediaUrl,
      title,
      thumbnailUrl: thumbnailUrl || '',
      timeToResume: hasStartedPlaying
        ? player.currentTime
        : videoDefaultProps.timeToResume || 0,
      isVideoEnded: videoEnded.current,
      isVertical,
      qualityOptions,
      isPlaying,
      hasStartedPlaying,
    })
  }, [
    player,
    setIsFullScreenMode,
    isFullScreenMode,
    setVideoDefaultProps,
    videoUrl,
    mediaUrl,
    title,
    thumbnailUrl,
    hasStartedPlaying,
    videoDefaultProps.timeToResume,
    isVertical,
    qualityOptions,
    isPlaying,
  ])

  const toggleMute = useCallback(() => {
    if (media.gtMd) {
      setShowVolumeSelector(!showVolumeSelector)
    } else {
      player.muted = !isMuted.current
      isMuted.current = !isMuted.current
    }
  }, [isMuted, media.gtMd, player, showVolumeSelector])

  const handleVolumeValueChange = useCallback(
    (value: number) => {
      player.volume = value / 100
      setVolume(value)
    },
    [player],
  )

  const errorHasOccurred = useMemo(() => {
    return player.status === 'error'
  }, [player.status])

  const formatTime = useMemo(() => {
    const hours = Math.floor(currentTime / 3600)
    const minutes = Math.floor((currentTime % 3600) / 60)
    const seconds = Math.floor(currentTime % 60)

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }

    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [currentTime])

  const handlePictureInPicture = useCallback(async () => {
    try {
      if (videoRef.current) {
        await videoRef.current.startPictureInPicture()
      }
    } catch (error) {
      throw new Error(t('error.pictureInPicture', { error }))
    }
  }, [videoRef, t])

  const handleAutoPlay = useCallback(() => {
    setIsAutoPlayActive((prev: boolean) => {
      if (prev) {
        removeItem()
      } else {
        setItem(prev.toString())
      }
      return !prev
    })
  }, [removeItem, setItem])

  const autoPlayIcon: IconProp = useMemo(
    () => (isAutoPlayActive ? ['fas', 'play'] : ['fas', 'pause']),
    [isAutoPlayActive],
  )
  const iconSizes = useMemo(
    () => ({
      bottomControls: media.gtMd ? 20 : 16,
      playPauseCenter: media.gtMd ? 64 : 24,
      advanceBackward: media.gtMd ? 32 : 24,
    }),
    [media.gtMd],
  )
  const playAndPauseIcon: IconProp = useMemo(
    () => (isPlaying ? ['fas', 'pause'] : ['fas', 'play']),
    [isPlaying],
  )
  const shouldMuteIcon = useMemo(() => {
    return isMuted.current || volume === 0
  }, [isMuted, volume])
  const volumeIcon: IconProp = useMemo(
    () => (shouldMuteIcon ? ['fal', 'volume-mute'] : ['fal', 'volume-high']),
    [shouldMuteIcon],
  )

  const controlBottomPosition = useMemo(() => {
    return isFullScreenMode ? safeAreaInsets.bottom : 0
  }, [isFullScreenMode, safeAreaInsets.bottom])

  const currentTimePercentage = useMemo(() => {
    return Math.max(0, (currentTime / Math.max(duration, 1)) * 100)
  }, [currentTime, duration])

  const videoTime = useMemo(() => {
    return (
      <XStack>
        <Text
          color="$cross-white"
          ff="$mulishRegular"
          fontSize="$font-size-xxs"
        >
          {formatTime}
        </Text>
      </XStack>
    )
  }, [formatTime])

  const watermark = useMemo(() => {
    if (!hasFeature || !hasStartedPlaying) return null

    const watermarkText =
      user?.cpf || `${user?.firstName} ${user?.lastName || ''}`

    return (
      <Watermark
        isPlaying={isPlaying}
        isFullScreenMode={isFullScreenMode}
        watermarkText={watermarkText}
      />
    )
  }, [hasFeature, hasStartedPlaying, isPlaying, isFullScreenMode, user])

  const CenterControls = useMemo(
    () => (
      <XStack
        position="absolute"
        width="100%"
        height="100%"
        justifyContent="center"
        alignItems="center"
        backgroundColor="rgba(0, 0, 0, 0.4)"
        gap="$spacing-inset-sm"
        zIndex={5}
        onPress={() => setShowControls(!showControls)}
      >
        <View
          onPress={() => handleSkip(-10)}
          role="button"
          aria-label={t('back10s')}
          justifyContent="center"
          alignItems="center"
          borderRadius="$border-radius-pill"
          padding="$spacing-inset-quarck"
        >
          <HeroIcon
            icon={'voltar10s'}
            size={iconSizes.advanceBackward}
            color="$cross-white"
          />
        </View>

        <View
          onPress={handlePlayPause}
          role="button"
          aria-label={t('playPause')}
          borderRadius="$border-radius-pill"
          padding="$spacing-inset-nano"
        >
          <HeroIcon
            icon={playAndPauseIcon}
            size={iconSizes.playPauseCenter}
            color="$cross-white"
          />
        </View>

        <View
          onPress={() => handleSkip(10)}
          role="button"
          aria-label={t('skip10s')}
          justifyContent="center"
          alignItems="center"
          borderRadius="$border-radius-pill"
          padding="$spacing-inset-quarck"
        >
          <HeroIcon
            icon={'avancar10s'}
            size={iconSizes.advanceBackward}
            color="$cross-white"
          />
        </View>
      </XStack>
    ),
    [
      handlePlayPause,
      handleSkip,
      iconSizes.advanceBackward,
      iconSizes.playPauseCenter,
      playAndPauseIcon,
      showControls,
      setShowControls,
      t,
    ],
  )

  const BottomControls = useMemo(
    () => (
      <>
        <View
          position="absolute"
          bottom={controlBottomPosition}
          left={0}
          right={0}
          paddingHorizontal="$spacing-inset-xs"
          zIndex={10}
          $gtMd={{
            flexDirection: 'column-reverse',
          }}
        >
          <XStack
            alignItems="center"
            justifyContent="space-between"
            zIndex={1}
            paddingLeft="$spacing-inset-xs"
          >
            {media.gtMd && (
              <XStack alignItems="center" gap="$spacing-inset-xs">
                <MediaControl
                  onPress={handlePlayPause}
                  aria-label={t('playPause')}
                >
                  <HeroIcon
                    icon={playAndPauseIcon}
                    size={iconSizes.bottomControls}
                    color="$cross-white"
                  />
                </MediaControl>

                <YStack position="relative">
                  <MediaControl
                    onPress={toggleMute}
                    aria-label={t('mute')}
                    position="relative"
                  >
                    <HeroIcon
                      icon={volumeIcon}
                      size={iconSizes.bottomControls}
                      color="$cross-white"
                    />
                    {showVolumeSelector && media.gtMd && (
                      <View position="absolute" bottom={50} left={20}>
                        <RangeInput
                          rangeColor="$cross-white"
                          value={volume}
                          onValueChange={handleVolumeValueChange}
                        />
                      </View>
                    )}
                  </MediaControl>
                </YStack>
              </XStack>
            )}

            {media.md && videoTime}

            <XStack alignItems="center" gap="$spacing-inset-xs">
              {media.gtMd && (
                <AutoPlaySwitch
                  isActive={isAutoPlayActive}
                  icon={autoPlayIcon}
                  onPress={() => handleAutoPlay()}
                />
              )}
              <MediaControl
                onPress={() => {
                  setShowConfigsModal(!showConfigsModal)
                  setShowQualitySelector(false)
                  setShowPlaybackRateSelector(false)
                }}
                aria-label={t('openQualityMenu')}
              >
                <HeroIcon
                  icon={['fal', 'cog']}
                  size={iconSizes.bottomControls}
                  color="$cross-white"
                />
              </MediaControl>

              <MediaControl
                onPress={handlePictureInPicture}
                aria-label={t('pictureInPicture')}
              >
                <HeroIcon
                  icon={'picture-in-picture'}
                  size={iconSizes.bottomControls}
                  color="$cross-white"
                />
              </MediaControl>

              <MediaControl
                onPress={() => handleFullScreen()}
                aria-label={t('fullscreen')}
              >
                <HeroIcon
                  icon={
                    isFullScreenMode ? ['fas', 'compress'] : ['fas', 'expand']
                  }
                  size={iconSizes.bottomControls}
                  color="$cross-white"
                />
              </MediaControl>
            </XStack>
          </XStack>
          <XStack>
            <XStack
              width="100%"
              position="relative"
              alignItems="center"
              gap="$spacing-inset-xxs"
              zIndex={1}
            >
              <View width="100%" position="relative">
                <RangeInput
                  rangeColor={rangeColor}
                  foregroundColor="rgba(207, 207, 207, 0.40)"
                  value={currentTimePercentage}
                  onValueChange={handleProgressChange}
                />
                <View
                  position="absolute"
                  width={`${bufferedPercentage - bufferedPlataformStyles().width}%`}
                  height={bufferedPlataformStyles().height}
                  backgroundColor="$neutral-500"
                  borderRadius="$border-radius-pill"
                  top="50%"
                  transform={bufferedPlataformStyles().transform}
                  zIndex={-10}
                />
              </View>
              {media.gtMd && videoTime}
            </XStack>
          </XStack>
        </View>
      </>
    ),
    [
      controlBottomPosition,
      media.gtMd,
      media.md,
      handlePlayPause,
      t,
      playAndPauseIcon,
      iconSizes.bottomControls,
      toggleMute,
      volumeIcon,
      showVolumeSelector,
      volume,
      handleVolumeValueChange,
      videoTime,
      isAutoPlayActive,
      autoPlayIcon,
      handlePictureInPicture,
      isFullScreenMode,
      rangeColor,
      currentTimePercentage,
      handleProgressChange,
      bufferedPercentage,
      handleAutoPlay,
      showConfigsModal,
      handleFullScreen,
    ],
  )

  const renderConfigsModal = useMemo(() => {
    if (!showConfigsModal) return null

    return (
      <ConfigsModal
        open={showConfigsModal}
        onClose={() => setShowConfigsModal(false)}
        openQualitySelector={() => setShowQualitySelector(true)}
        openSpeedSelector={() => setShowPlaybackRateSelector(true)}
        values={{
          quality: currentQuality.label,
          speed: playbackRate.toString(),
        }}
      />
    )
  }, [currentQuality.label, playbackRate, showConfigsModal])

  const renderQualitySelector = useMemo(() => {
    if (!showQualitySelector) return null

    return (
      <QualitySelector
        open={showQualitySelector}
        onClose={() => setShowQualitySelector(false)}
        qualityOptions={qualityOptions}
        onQualityChange={handleQualityChange}
        currentQuality={currentQuality}
      />
    )
  }, [currentQuality, handleQualityChange, qualityOptions, showQualitySelector])

  const renderPlaybackRateSelector = useMemo(() => {
    if (!showPlaybackRateSelector) return null

    return (
      <PlaybackRateSelector
        open={showPlaybackRateSelector}
        onClose={() => setShowPlaybackRateSelector(false)}
        playbackRateOptions={playbackRateOptions}
        onPlaybackRateChange={handlePlaybackRateChange}
        currentPlaybackRate={playbackRate}
      />
    )
  }, [
    handlePlaybackRateChange,
    playbackRate,
    playbackRateOptions,
    showPlaybackRateSelector,
  ])

  const renderThumbnail = useMemo(() => {
    if (hasStartedPlaying || isLoading || errorHasOccurred || isFullScreenMode)
      return null

    return (
      <View
        position="absolute"
        width="100%"
        height="100%"
        zIndex={3}
        backgroundColor="$cross-black"
        justifyContent="center"
        alignItems="center"
      >
        <View
          onPress={handlePlayPause}
          role="button"
          aria-label={t('playPause')}
          justifyContent="center"
          alignItems="center"
          cursor="pointer"
          position="absolute"
          flex={1}
          zIndex={4}
          borderRadius="$border-radius-pill"
          padding="$spacing-inset-xxs"
        >
          <HeroIcon
            icon={['fas', 'play']}
            size={iconSizes.playPauseCenter}
            color="$cross-white"
          />
        </View>
        <View
          position="absolute"
          width="100%"
          height="100%"
          zIndex={3}
          backgroundColor="rgba(0, 0, 0, 0.7)"
        />
        {thumbnailUrl && (
          <Image
            source={{ uri: thumbnailUrl }}
            style={{
              width: '100%',
              height: '100%',
            }}
            resizeMode="stretch"
            alt={title}
            fadeDuration={0}
          />
        )}
      </View>
    )
  }, [
    hasStartedPlaying,
    isLoading,
    errorHasOccurred,
    isFullScreenMode,
    handlePlayPause,
    t,
    iconSizes.playPauseCenter,
    thumbnailUrl,
    title,
  ])

  const renderVideoPlayer = useMemo(
    () => (
      <View
        height={isFullScreenMode ? fullscreenHeight : videoContainerHeight}
        width="100%"
        backgroundColor="$neutral-0"
        overflow="visible"
        position="relative"
        top={0}
        left={0}
        right={0}
        bottom={0}
        onHoverIn={() => setShowControls(true)}
        onHoverOut={() => isPlaying && setShowControls(false)}
      >
        <View
          style={[
            {
              flex: 1,
              position: 'relative',
              zIndex: 1,
              height: '100%',
            },
            isFullScreenMode && !isVertical ? videoStyles : null,
          ]}
        >
          {renderThumbnail}
          <Pressable
            style={{
              position: 'absolute',
              height: '100%',
              width: '100%',
              zIndex: 1,
            }}
            onPress={() => setShowControls(true)}
          />
          <View style={{ flex: 1, position: 'relative' }}>
            <VideoView
              ref={videoRef}
              player={player}
              style={{
                flex: 1,
                width: '100%',
                height: '100%',
              }}
              nativeControls={false}
              allowsFullscreen={!!media.gtMd}
              allowsPictureInPicture
              contentFit="contain"
            />
          </View>
          {watermark}
          {showControls && !showLoading && (
            <Animated.View
              style={[
                {
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  opacity: fadeAnim,
                  zIndex: 2,
                },
              ]}
            >
              <LinearGradient
                colors={['transparent', 'rgba(23, 23, 23, 0.8)']}
                style={{
                  width: '100%',
                  height: '100%',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                }}
              />
              {CenterControls}
              {BottomControls}
              {media.md && !isFullScreenMode && (
                <View position="absolute" top={8} right={16} zIndex={10}>
                  <AutoPlaySwitch
                    isActive={isAutoPlayActive}
                    icon={autoPlayIcon}
                    onPress={() => handleAutoPlay()}
                  />
                </View>
              )}
            </Animated.View>
          )}
          {showLoading && (
            <View
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              backgroundColor="rgba(0, 0, 0, 0.5)"
              justifyContent="center"
              alignItems="center"
              zIndex={1}
            >
              <Spinner size="large" color="$cross-white" />
            </View>
          )}
          {renderConfigsModal}
          {renderQualitySelector}
          {renderPlaybackRateSelector}
        </View>
      </View>
    ),
    [
      isFullScreenMode,
      fullscreenHeight,
      videoContainerHeight,
      isVertical,
      videoStyles,
      renderThumbnail,
      player,
      media.gtMd,
      media.md,
      watermark,
      showControls,
      showLoading,
      fadeAnim,
      CenterControls,
      BottomControls,
      isAutoPlayActive,
      autoPlayIcon,
      renderConfigsModal,
      renderQualitySelector,
      renderPlaybackRateSelector,
      isPlaying,
      handleAutoPlay,
    ],
  )

  if (isFullScreenMode) {
    return (
      <>
        <View height="100%" backgroundColor="$background-100" />
        <Sheet
          modal
          open={isFullScreenMode}
          snapPoints={[100, 100]}
          zIndex={100000}
          animation="medium"
          disableDrag
          dismissOnOverlayPress={false}
        >
          <Sheet.Overlay
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Sheet.Frame
            flex={1}
            padding={0}
            backgroundColor="$background-100"
            justifyContent="center"
            alignItems="center"
          >
            {renderVideoPlayer}
          </Sheet.Frame>
        </Sheet>
      </>
    )
  }

  return renderVideoPlayer
}

const MediaControl = styled(View, {
  role: 'button',
  cursor: 'pointer',
  hoverStyle: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  padding: '$spacing-inset-xxs',
})
